import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Typography } from '@mui/material';
import GoogleAuth from 'application/Login/Components/Containers/GoogleAuth';
import { Form, FormTextInput } from 'application/Shared/Components/Form';
import { FontSizes, ScopeTypes } from 'application/Shared/Constants';
import { Storage } from 'application/Shared/Handlers';
import { Response } from 'application/Shared/Helpers';
import Session from 'objects/Session';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ChooseGroups } from '../Steps/ChooseGroups';
import { excludeGroups, getGWSAPI } from './functions';
import { googleAutomatedIntegrationFormSchema } from './validator';

const { get } = Response;

const FormFields = ({
    accessToken,
    hidden,
    refreshToken,
    setUserNotAdmin,
    tenantToEditIntegration
}) => {
    const { t } = useTranslation();
    const { setValue } = useFormContext();

    const tenantToEditIntegrationDomains = tenantToEditIntegration?.domains;
    const tenantToEditIntegrationName = tenantToEditIntegration?.name;

    useEffect(() => {
        setValue('accessToken', accessToken);
        setValue('refreshToken', refreshToken);
    }, [accessToken, refreshToken, setValue]);

    useEffect(() => {
        const storeTokens = async () => {
            try {
                const api = getGWSAPI(accessToken);
                const response = await api.get('/customer/my_customer/domains');

                if (response?.data?.domains) {
                    const { domains } = get(response);
                    const domainNames = domains.map((a) => a.domainName);
                    const domainAliases = domains
                        .map(
                            (a) =>
                                a.domainAliases?.map(
                                    (b) => b.domainAliasName
                                ) || []
                        )
                        .flat();

                    // Join client's domains and the ones found from the integration
                    const combinedDomains = tenantToEditIntegrationDomains
                        ? domainNames
                              .concat(domainAliases)
                              .concat(tenantToEditIntegrationDomains)
                        : domainNames.concat(domainAliases);

                    // Remove duplicates
                    const uniqueDomains = Array.from(new Set(combinedDomains));

                    setValue('domains', uniqueDomains.join());
                    if (tenantToEditIntegrationDomains) {
                        setValue('updateDomains', true);
                    }
                    if (tenantToEditIntegrationName) {
                        setValue('name', tenantToEditIntegrationName);
                    }
                    setUserNotAdmin(false);
                }
            } catch (e) {
                if (e.config?.headers?.Authorization) {
                    e.config.headers.Authorization = '[REDACTED]';
                }
                console.log('Error fetching GWS domains', e);
                setUserNotAdmin(true);
            }
        };
        if (accessToken && refreshToken) {
            storeTokens();
        }
    }, [
        accessToken,
        hidden,
        refreshToken,
        setUserNotAdmin,
        setValue,
        tenantToEditIntegrationDomains,
        tenantToEditIntegrationName
    ]);

    return (
        !hidden && (
            <Box mx="24px" mb="24px">
                <FormTextInput
                    {...(tenantToEditIntegrationName
                        ? { inputProps: { readOnly: true } }
                        : {})}
                    label={t('Common.client_name')}
                    name="name"
                    placeholder={t('Common.brand_name')}
                    required
                />
                <FormTextInput
                    inputProps={{ readOnly: true }}
                    label={t('Clients.domain(s)')}
                    name="domains"
                    placeholder="saas.com,saas.onmicrosoft.com"
                    required
                    tooltipText={t('Tooltips.domains_comma_separated')}
                />
            </Box>
        )
    );
};

export const GoogleAutomatedIntegrationForm = ({
    children,
    clientId,
    integrationAuthenticated,
    onboarding,
    onSubmit,
    tenantToEditIntegration,
    useIntegrationWizard
}) => {
    const { t } = useTranslation();
    const [accessToken, setAccessToken] = useState(null);
    const [refreshToken, setRefreshToken] = useState(null);
    const [scopeOk, setScopeOk] = useState(null);
    const [userNotAdmin, setUserNotAdmin] = useState(null);
    const [groups, setGroups] = useState({});

    const providerRoute = 'google';

    const handleStorage = useCallback(async (message) => {
        const { key } = message;
        // using the helper here so that the token is extracted in a consistent manor and we don't have to duplicate the parsing logic.
        if (key === 'gwsAccessToken') {
            const token = await Storage.get(key);
            setAccessToken(token);
        }
        if (key === 'gwsRefreshToken') {
            const token = await Storage.get(key);
            setRefreshToken(token);
        }
        if (key === 'gwsScopeOk') {
            const scopeOkLocal = await Storage.get(key);
            setScopeOk(scopeOkLocal);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('storage', handleStorage);
        return () => {
            window.removeEventListener('storage', handleStorage);
        };
    }, [handleStorage]);

    useEffect(() => {
        const fetchAccessToken = async () => {
            const tokens = await Session.getGoogleAppAuthDetails();
            if (tokens) {
                const {
                    accessToken: accessTokenLocal,
                    refreshToken: refreshTokenLocal,
                    scopeOk: scopeOkLocal
                } = tokens;
                setAccessToken(accessTokenLocal);
                setRefreshToken(refreshTokenLocal);
                setScopeOk(scopeOkLocal);
            }
        };

        fetchAccessToken();
    }, []);

    const onDone = async (values) => {
        const providerInfo = {
            accessToken
        };
        const success = await excludeGroups({
            clientId,
            groups,
            onboarding,
            providerInfo,
            providerRoute,
            t,
            useIntegrationWizard
        });
        if (success) {
            await onSubmit(values);
        }
    };

    const hidden = !useIntegrationWizard && !onboarding;

    const validator = !hidden
        ? yupResolver(googleAutomatedIntegrationFormSchema({ t }))
        : null;

    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            height="100%"
        >
            <Box mx="32px" mb="24px">
                <GoogleAuth scopeType={ScopeTypes.AppAuth} />
                <Typography
                    m="12px"
                    variant="body2"
                    style={{
                        fontSize: FontSizes[1]
                    }}
                    dangerouslySetInnerHTML={{
                        __html: t('Provider.GoogleConfiguration.disclaimer')
                    }}
                />
                {scopeOk === 'false' && (
                    <Alert
                        severity="error"
                        icon={false}
                        style={{
                            marginBottom: '16px',
                            marginTop: '16px'
                        }}
                    >
                        {t('Provider.GoogleConfiguration.scope_error')}
                    </Alert>
                )}
                {userNotAdmin && (
                    <Alert
                        severity="error"
                        icon={false}
                        style={{
                            marginBottom: '16px',
                            marginTop: '16px'
                        }}
                    >
                        {t('Provider.GoogleConfiguration.permission_error')}
                    </Alert>
                )}
                {integrationAuthenticated && (
                    <ChooseGroups
                        accessToken={accessToken}
                        groups={groups}
                        integrationType="googleAutomated"
                        linkedTenantId={clientId}
                        providerRoute={providerRoute}
                        setGroups={setGroups}
                    />
                )}
            </Box>

            <Form
                defaultValues={{
                    accessToken,
                    domains: '',
                    name: '',
                    providerType: 'Google',
                    refreshToken,
                    updateDomains: null
                }}
                validator={validator}
                onSubmit={onDone}
                style={{
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {accessToken && refreshToken && scopeOk === 'true' && (
                    <FormFields
                        accessToken={accessToken}
                        hidden={hidden}
                        refreshToken={refreshToken}
                        setUserNotAdmin={setUserNotAdmin}
                        tenantToEditIntegration={tenantToEditIntegration}
                    />
                )}
                {children}
            </Form>
        </Box>
    );
};
